/**
 * Events
 * Create the Events section
 *
 * Usage
 * - items is the main prop that gets all the events
 * - Accepts a GraphQL feed from contentful to populate the list of events, this
 *   list is used create the month association so its dynamic.
 *
 * GraphQL
 * ContentfulEvent {
 *   title
 *   startDate
 *   endDate
 *   locationAddress
 *   eventType
 *   locationName
 *   image {
 *     title
 *     fixed {
 *       src
 *     }
 *   }
 * }
 *
 * Features
 * - Filterable event Types (One selectedable only)
 * - Sortable, current and past
 *   - current endDate > DateNow
 *   - past endDate < DateNow
 *   - If no end date then assume current
 *
 * @todo Refactor: could add the month key into the reduced filter
 * @todo Seems I get the whole events list back so interactions here is just dealing with the graphql stack
 * @todo Background image from Fitch CDN is 700KB ! this should be optimised
 * @todo Images need fixed here - they are huge in filesize
 * @todo Currently have a nested section cause the Landing page brings this in with the section, but at that stage I cannot add the bits needed
 * @todo Not sure these lists comply with the List component so hardlining in EventFilters
 * @todo the Date sorting only needs to run intially on first render - now I am running each time sort or filtered
 */

import React, { Component, Fragment } from 'react'
import includes from 'lodash/includes'

import { Hash, Header, Section, FormatMonth } from '../..'
import { onButtonTriggerKey } from '../../../utils'
import {
  augmentEvent,
  Event,
  EventSecReg
} from '../../../utils/contentful-events'
import { WithScroller } from '../../WithScroller'
import { EventListing, EventImage } from './EventListing'
import { EventListingsFilters } from './EventListingsFilters'
import { EventListingsMonths } from './EventListingsMonths'
import {
  EventListingsFilterType,
  FilterSubItemProps
} from './EventListingsFilterType'

import { eventTypes, sortTypes, filSecRegTypes } from './constants'

const noop = () => {}

type EventProps = Event & {
  image: EventImage
}

export type EventRegSec = {
  items: any[]
  value: string
}
export type EventsProps = {
  secFilType: EventRegSec
  regFilType: EventRegSec
  isHomePage?: boolean
  items: EventProps[]
  useRelativeUrl: boolean
  eventLangSlug: string
  eventsDataLoading?: boolean
  hideFilters?: boolean
  bgImage?: string
  dataProcessComplete?: () => void
}
export type FilterFlags = {
  haveBothRegCountryFilter: boolean
  haveRegionFilter: boolean
  haveCountryFilter: boolean
  haveSectorFilter: boolean
}

interface EventsState {
  activeMonth: string
  filter: string[]
  filteredItems: EventProps[]
  isLoading: boolean
  months: string[]
  sort: string
  filSecReg: string
  isFilterType: boolean
  selectedSecFilters: any[]
  selectedRegFilters: any[]
  selectedCountryFilters: any[]
  filterOverlay: boolean
}

const handleFilter = (
  list: EventSecReg[],
  filteredList: any,
  callback: () => void
) => {
  list.forEach((item) => {
    if (filteredList.includes(item.title)) {
      callback()
    }
  })
}

const eventSecMatches = (
  eventSecList: EventSecReg[],
  filteredEventSecList: any
): boolean => {
  return eventSecList.some((item: EventSecReg) =>
    filteredEventSecList.includes(item.title)
  )
}

const getFilteredItems = (
  filteredSecList: string | any[],
  filteredRegList: string | any[],
  filteredCountryList: string | any[],
  filteredReturnArray: EventProps[],
  reducedItems: EventProps[]
) => {
  const hasSectorFilter = filteredSecList && filteredSecList.length > 0
  const hasRegionFilter = filteredRegList && filteredRegList.length > 0
  const hasCountryFilter = filteredCountryList && filteredCountryList.length > 0

  return hasSectorFilter || hasRegionFilter || hasCountryFilter
    ? filteredReturnArray
    : reducedItems
}
const getFilteredReturnArray = (
  reducedItems: EventProps[],
  filteredRegList: any,
  filteredCountryList: any,
  filterFlags: FilterFlags,
  filteredSecList: any
) => {
  return reducedItems.filter((item) => {
    let matches = false
    const sectorList = item.sectors
    const regionList = item.regions
    const countryList = item.countries
    const {
      haveBothRegCountryFilter,
      haveRegionFilter,
      haveCountryFilter,
      haveSectorFilter
    } = filterFlags

    const bothRegAndCountryMatches = () => {
      regionList.forEach((regItem) => {
        if (filteredRegList.includes(regItem.title)) {
          matches = eventSecMatches(countryList, filteredCountryList)
        }
      })
    }
    const filteredByRegion = () => {
      if (haveBothRegCountryFilter && regionList && countryList) {
        bothRegAndCountryMatches()
        return
      }
      if (haveRegionFilter && regionList) {
        matches = eventSecMatches(regionList, filteredRegList)
        return
      }
      if (haveCountryFilter && countryList) {
        matches = eventSecMatches(countryList, filteredCountryList)
      }
    }

    const filterByRegionAndSector = () => {
      if (!sectorList || !haveSectorFilter) {
        filteredByRegion()
        return
      }
      matches = eventSecMatches(sectorList, filteredSecList)
      handleFilter(sectorList, filteredSecList, filteredByRegion)
    }
    filterByRegionAndSector()
    return matches
  })
}
export class EventListings extends Component<EventsProps, EventsState> {
  state: Readonly<EventsState> = {
    activeMonth: '',
    filter: ['all', ''],
    filteredItems: [],
    isLoading: true,
    months: [],
    sort: 'upcoming',
    filSecReg: '',
    isFilterType: false,
    selectedSecFilters: [],
    selectedRegFilters: [],
    selectedCountryFilters: [],
    filterOverlay: false
  }

  componentDidMount() {
    this.processData()
  }

  componentDidUpdate(prevProps: { items: EventProps[] }) {
    // to update component basd on new props
    const { items } = this.props
    if (items !== prevProps.items) {
      this.processData()
    }
  }

  updateFilteredValue = (
    filter: string[],
    filteredSecList: any,
    filteredRegList: any,
    filteredCountryList: any
  ) => {
    const replacedString = window.location.search
      .substring(1)
      .replace(/%20/g, ' ')
    const searchList = replacedString ? replacedString.split('&') : []
    let updatedSortvalue = ''

    /* eslint-disable no-param-reassign  */
    searchList.forEach((search: string) => {
      if (search !== '') {
        const [searchKey = '', searchValue = ''] = search.split('=')
        switch (searchKey) {
          case 'eventType':
            filter[1] = searchValue
            filter[0] = searchValue.toLowerCase()
            break
          case 'sort':
            updatedSortvalue = searchValue
            break
          case 'sector':
            filteredSecList.push(searchValue)
            break
          case 'region':
            filteredRegList.push(searchValue)
            break
          case 'country':
            filteredCountryList.push(searchValue)
            break
          default:
          // No default
        }
      }
    })

    if (filteredSecList && filteredSecList.length > 0) {
      this.setState({ selectedSecFilters: filteredSecList })
    }
    if (filteredRegList && filteredRegList.length > 0) {
      this.setState({ selectedRegFilters: filteredRegList })
    }
    if (filteredCountryList && filteredCountryList.length > 0) {
      this.setState({ selectedCountryFilters: filteredCountryList })
    }
    return updatedSortvalue
  }

  sortAndReduce = (updatedSortValue: string, monthsList: any) => {
    const buildDate: number = Date.now()
    const { items, useRelativeUrl } = this.props
    const { filter, sort } = this.state
    if (updatedSortValue === '') {
      updatedSortValue = sort
    }
    const augmentOptions = { useRelativeUrl }
    const sortDirection = updatedSortValue === 'past' ? -1 : 1
    const sortedItems = items
      .map((e) => augmentEvent(e, augmentOptions))
      .map((e) => ({
        ...e,
        startMillis: new Date(e.startDate).getTime(),
        endMillis: e.endDate ? new Date(e.endDate).getTime() : Number.NaN
      }))
      .sort((a, b) => (a.startMillis - b.startMillis) * sortDirection)
    return sortedItems.reduce<EventProps[]>((r, item) => {
      const eventDate: number = item.endMillis || item.startMillis
      const sorted =
        updatedSortValue === 'upcoming' || updatedSortValue === ''
          ? buildDate <= eventDate
          : buildDate >= eventDate
      const filtered =
        filter[0] === 'all' ? true : includes(filter[1], item.eventType)
      if (sorted && filtered) {
        r.push(item)
        monthsList.push(FormatMonth(item.startDate))
      }
      return r
    }, [])
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  processData = () => {
    const { filter } = this.state
    const monthsList: any = []
    const filteredSecList: any = []
    const filteredRegList: any = []
    const filteredCountryList: any = []
    const updatedSortValue = this.updateFilteredValue(
      filter,
      filteredSecList,
      filteredRegList,
      filteredCountryList
    )
    if (updatedSortValue) {
      this.setState({ sort: updatedSortValue })
    }
    const reducedItems = this.sortAndReduce(updatedSortValue, monthsList)
    const haveBothRegCountryFilter =
      filteredRegList &&
      filteredRegList.length > 0 &&
      filteredCountryList &&
      filteredCountryList.length > 0

    const haveRegionFilter = filteredRegList && filteredRegList.length > 0
    const haveCountryFilter =
      filteredCountryList && filteredCountryList.length > 0
    const haveSectorFilter = filteredSecList && filteredSecList.length > 0

    const filterFlags: FilterFlags = {
      haveBothRegCountryFilter,
      haveRegionFilter,
      haveCountryFilter,
      haveSectorFilter
    }
    const filteredReturnArray = getFilteredReturnArray(
      reducedItems,
      filteredRegList,
      filteredCountryList,
      filterFlags,
      filteredSecList
    )

    this.setState((prevState) => ({
      ...prevState,
      activeMonth: monthsList[0],
      filteredItems: getFilteredItems(
        filteredSecList,
        filteredRegList,
        filteredCountryList,
        filteredReturnArray,
        reducedItems
      ),
      isLoading: false,
      months: monthsList
    }))
    const dataProcessComplete = this.props.dataProcessComplete || noop
    dataProcessComplete()
  }

  clickFilter = (type: string, include?: string[]) => {
    const data: any = [type, include]
    this.setState(
      (prevState) => ({
        ...prevState,
        filter: data,
        isFilterType: false,
        filterOverlay: false
      }),
      () => this.applyFilterUrl()
    )
  }

  clickSort = (type: string) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        sort: type,
        isFilterType: false,
        filterOverlay: false
      }),
      () => this.applyFilterUrl()
    )
  }

  clickFilRegSec = (type: string) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        filSecReg: type,
        isFilterType: true,
        filterOverlay: true
      }),
      () => this.processData()
    )
  }

  clickMonth = (month: string) => {
    this.setState((prevState) => ({
      ...prevState,
      activeMonth: month
    }))
  }

  applyFilterUrl = () => {
    const {
      filter,
      sort,
      selectedSecFilters,
      selectedRegFilters,
      selectedCountryFilters,
      filSecReg
    } = this.state
    const { eventLangSlug } = this.props
    const regionLangSlug = eventLangSlug !== 'en' ? eventLangSlug : ''
    let filterString = ''
    if (filter && filter.length > 0) {
      const [eventType = '', eventTypeURLParam = ''] = filter
      filterString =
        eventType !== 'all'
          ? filterString.concat(`eventType=${eventTypeURLParam}&`)
          : ''
    }
    if (sort && sort !== '' && sort !== 'upcoming') {
      filterString = filterString.concat(`sort=${sort}&`)
    }
    if (selectedSecFilters.length > 0) {
      selectedSecFilters.forEach((filterValue: string) => {
        filterString = filterString.concat(`sector=${filterValue}&`)
      })
    }
    if (selectedRegFilters.length > 0) {
      selectedRegFilters.forEach((filterValue: string) => {
        filterString = filterString.concat(`region=${filterValue}&`)
      })
    }
    if (selectedCountryFilters.length > 0) {
      selectedCountryFilters.forEach((filterValue: string) => {
        filterString = filterString.concat(`country=${filterValue}&`)
      })
    }
    const urlparams = regionLangSlug ? `/${regionLangSlug}/events?` : `/events?`
    if (filSecReg) {
      window.location.assign(`${urlparams}${filterString}`)
    } else if (window.location.pathname.includes('events')) {
      const newurl = `${window.location.protocol}//${window.location.host}${urlparams}${filterString}`
      window.history.pushState({ path: newurl }, '', newurl)
      this.processData()
    } else {
      this.processData()
    }
  }

  updateFilters = (
    isAlreadySelected: boolean,
    selectedItem: FilterSubItemProps
  ) => {
    const { selectedSecFilters, selectedRegFilters, selectedCountryFilters } =
      this.state
    const { filterType } = selectedItem
    let updatedSelectedSecFilters: any
    let updatedSelectedRegFilters: any
    let updateSelectedCountryFilters: any
    const updateSelection = (data: string[], applyUniqueSelection = false) => {
      let result: string[] = data
      if (isAlreadySelected) {
        result = data.filter((item) => item !== selectedItem.value)
      } else {
        if (applyUniqueSelection) result.shift()
        result.push(selectedItem.value)
      }
      return result
    }
    if (filterType === 'sector') {
      updatedSelectedSecFilters = updateSelection(selectedSecFilters)
      this.setState({ selectedSecFilters: updatedSelectedSecFilters })
    }
    if (filterType === 'region') {
      updatedSelectedRegFilters = updateSelection(selectedRegFilters)
      this.setState({ selectedRegFilters: updatedSelectedRegFilters })
    }
    if (filterType === 'country') {
      updateSelectedCountryFilters = updateSelection(selectedCountryFilters)
      this.setState({ selectedCountryFilters: updateSelectedCountryFilters })
    }
  }

  isItemSelected = (value: string) => {
    const {
      selectedRegFilters,
      selectedCountryFilters,
      selectedSecFilters,
      filSecReg
    } = this.state
    let result: boolean
    if (filSecReg === 'sector') {
      result = selectedSecFilters.includes(value)
    } else {
      result =
        selectedRegFilters.includes(value) ||
        selectedCountryFilters.includes(value)
    }
    return result
  }

  handleSubmitClick = () => {
    this.applyFilterUrl()
  }

  handleResetClick = () => {
    const { eventLangSlug } = this.props
    const regionLangSlug = eventLangSlug !== 'en' ? eventLangSlug : ''
    const urlparams = regionLangSlug ? `/${regionLangSlug}/events` : `/events`
    window.location.assign(urlparams)
  }

  handleOnCloseClick = () => {
    this.setState({ filterOverlay: false })
  }

  showFilters = () => {
    const { filSecReg } = this.state
    const { secFilType, regFilType } = this.props
    const { items, value } = filSecReg === 'sector' ? secFilType : regFilType
    return (
      <>
        <div className="frw-event__overlay-title">{value}</div>
        <div className="frw-event__filter-wrapper">
          <WithScroller>
            <>
              <ul className="frw-event__filter">
                {items &&
                  items.length &&
                  items.map((subItem) => (
                    <EventListingsFilterType
                      filterType={value}
                      filterItems={subItem}
                      isChecked={this.isItemSelected(subItem.value)}
                      updateFilters={this.updateFilters}
                      key={subItem.label}
                    />
                  ))}
              </ul>
            </>
          </WithScroller>
        </div>
        <div className="frw-button__wrapper">
          <button
            type="button"
            className="frw-button frw-button--6-1"
            data-testid="button--6-1"
            onClick={this.handleResetClick}
            style={{ width: '200px' }}
          >
            Reset
          </button>
          <button
            type="button"
            className="frw-button frw-button--3"
            onClick={this.handleSubmitClick}
            style={{ width: '200px' }}
          >
            Submit
          </button>
        </div>
        <div
          onClick={this.handleOnCloseClick}
          onKeyDown={onButtonTriggerKey(this.handleOnCloseClick)}
          role="button"
          tabIndex={0}
          className="frw-event__overlay-close"
        >
          <i data-testid="close-modal" className="fas fa-times" />
        </div>
      </>
    )
  }

  mapEvents = () => {
    const { filteredItems } = this.state
    return filteredItems.map((item: EventProps, index: number) => {
      const { startDate } = item
      const month = FormatMonth(startDate)
      const hash = Hash(['eventMap', startDate, index].join('#'))
      const fi: any = filteredItems[index - 1]
      const fii: any = filteredItems[index]
      const monthPrev = index > 0 ? fi.startDate : fii.startDate
      return (
        <Fragment key={hash}>
          {(index === 0 || FormatMonth(monthPrev) !== month) && (
            <div
              className="frw-event__row--date frw-event__row--date---1"
              id={month}
            >
              {month}
            </div>
          )}
          <EventListing {...item} />
        </Fragment>
      )
    })
  }

  getEventClassName = () => {
    const { filterOverlay } = this.state
    const { isHomePage, hideFilters } = this.props
    let eventSectionClassName: string
    if (isHomePage || hideFilters) {
      if (filterOverlay) {
        eventSectionClassName = 'frw-section__events frw-active'
      } else {
        eventSectionClassName = 'frw-section__events'
      }
    } else if (filterOverlay) {
      eventSectionClassName =
        'frw-section__events frw-section__events--header frw-active'
    } else {
      eventSectionClassName = 'frw-section__events frw-section__events--header'
    }
    return eventSectionClassName
  }

  render() {
    const FRBackgroundImage =
      "url('https://images.ctfassets.net/03fbs7oah13w/1duv1G8KG9R9YS70znGcwt/d7f31c88d7e9c46e5034e4dc8477a0c4/fr-page-header-001.jpg')" +
      ',' +
      'linear-gradient(108deg, #3e5151 0%,#decba4 100%)'
    const {
      eventsDataLoading,
      hideFilters = false,
      bgImage = FRBackgroundImage
    } = this.props
    const {
      activeMonth,
      filter,
      filteredItems,
      isLoading,
      months,
      sort,
      filSecReg,
      filterOverlay,
      selectedRegFilters,
      selectedCountryFilters,
      selectedSecFilters
    } = this.state
    let filteredBytype = filSecReg
    if (!filterOverlay) {
      if (
        (selectedRegFilters.length > 0 || selectedCountryFilters.length > 0) &&
        selectedSecFilters.length === 0
      ) {
        filteredBytype = 'region'
      } else if (
        selectedSecFilters.length > 0 &&
        selectedRegFilters.length === 0
      ) {
        filteredBytype = 'sector'
      }
    }

    const bgImageStyle = {
      backgroundImage: bgImage
    }

    const eventClassName = this.getEventClassName()
    return (
      <Section className={eventClassName}>
        <div
          className="frw-event__background frw-bg--blend"
          style={bgImageStyle}
        />
        <div className="frw-wrapper--1 frw-content frw-clearfix">
          <div className="frw-event__wrapper--title">
            <Header className="frw-heading--2" level={1}>
              Events
            </Header>
            {!isLoading && (
              <>
                <nav aria-label="Event filters">
                  <EventListingsFilters
                    filter={filter}
                    handleClick={this.clickFilter}
                    items={eventTypes}
                    filterOverlay={filterOverlay}
                    title="Event Type"
                  />
                  <EventListingsFilters
                    handleClick={this.clickSort}
                    items={sortTypes}
                    sort={sort}
                    filterOverlay={filterOverlay}
                    title="Sort"
                  />
                  <div hidden={hideFilters}>
                    <EventListingsFilters
                      handleClick={this.clickFilRegSec}
                      data-testid="event-listing-filter"
                      items={filSecRegTypes}
                      filterOverlay={filterOverlay}
                      filteredBytype={filteredBytype}
                      title="Filter"
                    />
                  </div>
                </nav>
              </>
            )}
          </div>
          <div className="frw-quicktabs-tabpage">
            <div
              id="event__wrapper--row-wrapper"
              className="frw-event__wrapper--row-wrapper"
            >
              <WithScroller>
                <div className="frw-event__wrapper--row">
                  <div className="frw-event__row--group">
                    {filteredItems && filteredItems.length ? (
                      this.mapEvents()
                    ) : (
                      <div className="frw-event__row--date">
                        {eventsDataLoading ? 'Loading Events...' : 'No Events'}
                      </div>
                    )}
                  </div>
                </div>
              </WithScroller>
            </div>
            <EventListingsMonths
              active={activeMonth}
              handleClick={this.clickMonth}
              months={months}
              sort={sort}
            />
          </div>
          <div className="frw-event__overlay" hidden={!filterOverlay}>
            {this.showFilters()}
          </div>
        </div>
      </Section>
    )
  }
}
